/**
 * Hablar con el director como se habla por teléfono.
 *
 * Dices algo, se calla, contesta en voz alta, y vuelve a escuchar. Sin tocar
 * nada y con el móvil en el bolsillo.
 *
 * **La diferencia con el manos libres no es el micrófono: es no cerrarlo.**
 *
 * Ahí se abre y se cierra en cada frase, y ese abrir y cerrar es lo que hace
 * que un auricular Bluetooth cambie de modo música a modo llamada cada vez —
 * que es donde han estado todos los fallos de la mañana del 17/08/2026:
 * grabaciones mudas, botones que no llegaban, perfiles a medio cambiar. Aquí el
 * micrófono se abre UNA VEZ al empezar y se cierra al terminar. El aparato se
 * queda en modo llamada de principio a fin, que es justo para lo que se diseñó:
 * oír y hablar a la vez.
 *
 * Lo único que cambia entre turnos es qué se hace con lo que entra.
 */

/** A partir de qué volumen se considera que alguien está hablando. */
const UMBRAL = 0.06;

/**
 * Cuánto silencio cierra una frase.
 *
 * 1,4 s aquí y 3 s con el botón, y la diferencia es a propósito: **esto es una
 * conversación**. Cada décima de este número se suma a TODAS las respuestas, así
 * que es de lejos lo que más pesa en la sensación de que va rápido o lento —
 * más que el modelo y más que la voz.
 *
 * Dictando un encargo largo se piensa a mitad y cortar ahí parte el mensaje;
 * hablando de ida y vuelta, las frases son cortas y la pausa se hace eterna.
 */
const SILENCIO_MS = 1400;

/**
 * Lo mínimo para dar una frase por buena.
 *
 * **Guardia 4.** En la calle entra ruido: un coche, una puerta, alguien que
 * pasa. Sin esto, cada portazo sería una frase que se transcribe, se manda al
 * modelo y se contesta en voz alta — te interrumpiría el paseo para responder a
 * un ruido, y encima pagando.
 */
const MINIMO_MS = 600;

/** Y por si acaso: una frase no dura cinco minutos. */
const MAXIMO_MS = 120_000;

export interface Conversacion {
  /**
   * Espera a la siguiente frase y la devuelve.
   *
   * `null` significa que se ha terminado la conversación mientras escuchaba.
   */
  escuchar(): Promise<Blob | null>;
  /** Deja de hacer caso al micrófono: está hablando él. */
  silenciar(): void;
  /** Vuelve a escuchar. */
  reanudar(): void;
  /** Cierra el micrófono y suelta el aparato. */
  colgar(): void;
  /** El volumen de ahora, para pintar algo que se mueva. */
  nivel(): number;
}

export function sePuedeConversar(): boolean {
  return (
    typeof MediaRecorder !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

export async function conversar(): Promise<Conversacion> {
  const flujo = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });

  const audio = new AudioContext();
  const analizador = audio.createAnalyser();
  analizador.fftSize = 512;
  audio.createMediaStreamSource(flujo).connect(analizador);
  const muestras = new Uint8Array(analizador.frequencyBinCount);

  let vivo = true;
  let sordo = false;
  let nivelActual = 0;

  const midiendo = () => {
    if (!vivo) return;
    analizador.getByteTimeDomainData(muestras);
    let pico = 0;
    for (const v of muestras) pico = Math.max(pico, Math.abs(v - 128) / 128);
    nivelActual = sordo ? 0 : pico;
    requestAnimationFrame(midiendo);
  };
  requestAnimationFrame(midiendo);

  function formato(): string {
    for (const tipo of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
      if (MediaRecorder.isTypeSupported(tipo)) return tipo;
    }
    return "";
  }

  return {
    nivel: () => nivelActual,

    silenciar() {
      sordo = true;
    },
    reanudar() {
      sordo = false;
    },

    colgar() {
      vivo = false;
      void audio.close().catch(() => {});
      flujo.getTracks().forEach((t) => t.stop());
    },

    escuchar(): Promise<Blob | null> {
      return new Promise((listo) => {
        const tipo = formato();
        const grabadora = new MediaRecorder(flujo, tipo ? { mimeType: tipo } : undefined);
        const trozos: BlobPart[] = [];
        grabadora.ondataavailable = (e) => {
          if (e.data.size > 0) trozos.push(e.data);
        };

        let hablando = false;
        let calladoDesde: number | null = null;
        let desdeQueHabla = 0;
        const empezado = Date.now();

        const terminar = (valido: boolean) => {
          clearInterval(vigilante);
          grabadora.onstop = () =>
            listo(valido ? new Blob(trozos, { type: grabadora.mimeType || "audio/webm" }) : null);
          if (grabadora.state !== "inactive") grabadora.stop();
          else listo(null);
        };

        const vigilante = setInterval(() => {
          if (!vivo) return terminar(false);
          if (Date.now() - empezado > MAXIMO_MS) return terminar(hablando);

          // Mientras habla él, lo que entra no cuenta. Guardia 3.
          if (sordo) return;

          if (nivelActual > UMBRAL) {
            if (!hablando) desdeQueHabla = Date.now();
            hablando = true;
            calladoDesde = null;
            return;
          }

          // El silencio de antes de empezar no cuenta: se piensa antes de hablar.
          if (!hablando) return;

          calladoDesde ??= Date.now();
          if (Date.now() - calladoDesde < SILENCIO_MS) return;

          /**
           * Se acabó la frase. Si fue un ruido y no una frase, se tira y se
           * sigue escuchando sin molestar a nadie.
           */
          const duro = (calladoDesde ?? Date.now()) - desdeQueHabla;
          if (duro < MINIMO_MS) {
            hablando = false;
            calladoDesde = null;
            trozos.length = 0;
            return;
          }
          terminar(true);
        }, 100);

        grabadora.start(200);
      });
    },
  };
}
